import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {css, CSSProperties, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';

import {
  ConsentPreferences,
  ConsentState,
  Destination,
  destinationById,
  DestinationCategory,
  DestinationId,
} from 'analytics/Destinations';
import {assert} from 'Asserts';
import {LEGACY_FONT_PROXIMA_13, LEGACY_FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY} from 'ui-library/Colors';
import {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';
import Modal from 'ui-library/modals/Modal';
import * as modals from 'util/Modals';

function Heading({children}: {children: ReactNode}): JSX.Element {
  return <th className={css(styles.tableHeading)}>{children}</th>;
}

function Cell({children, dataTestAttrs}: {children?: ReactNode; dataTestAttrs?: DataTestAttrs}): JSX.Element {
  return (
    <td className={css(styles.tableCell)} {...getTestProps(dataTestAttrs ?? {})}>
      {children}
    </td>
  );
}

export interface CookieConsentModalProps {
  onRequestSubmit(newPreferences: ConsentPreferences): void;
  onRequestClose(): void;
  destinationIds: Array<string>;
  initialPreferences: ConsentPreferences;
}

interface State {
  prefs: ConsentPreferences;
}

/**
 * Modal asking for consent for each category of tracking.
 *
 * Part of the copy is in part taken from https://github.com/segmentio/consent-manager
 * MIT license: https://github.com/segmentio/consent-manager/blob/master/LICENSE
 */
export default class CookieConsentModal extends React.Component<CookieConsentModalProps, State> {
  override state: State = {
    prefs: this.getInitialPrefs(),
  };

  /**
   * If the prefs disagree, default to no selection for the category. This might happen if new
   * destinations are added, and forces the user to agree to the new ones.
   */
  private getInitialPrefs(): ConsentPreferences {
    const prefs: ConsentPreferences = {};
    for (const category of Object.values(DestinationCategory)) {
      const destinations = this.getDestinationsForCategory(category);
      const prefValues = destinations.map(
        (destination) => this.props.initialPreferences[destination.id] || ConsentState.UNSPECIFIED
      );
      let categorySetting = new Set(prefValues).size === 1 ? prefValues[0] : ConsentState.UNSPECIFIED;
      // If the user previously implicitly agreed, the modal represents it as unspecified (neither
      // radio button selected). IMPLICIT_AGREE is only valid outside the modal.
      if (categorySetting === ConsentState.IMPLICIT_AGREE) {
        categorySetting = ConsentState.UNSPECIFIED;
      }
      for (const destination of destinations) {
        prefs[destination.id] = categorySetting;
      }
    }
    return prefs;
  }

  private getDestinationsForCategory(category: DestinationCategory): Array<Destination> {
    return this.props.destinationIds.map((id) => destinationById(id)).filter((d) => d.category === category);
  }

  private handleSetPreference(category: DestinationCategory, value: ConsentState): void {
    const prefs = {...this.state.prefs};
    for (const {id} of this.getDestinationsForCategory(category)) {
      prefs[id] = value;
    }
    this.setState({prefs});
  }

  private getCategoryPreference(category: DestinationCategory): ConsentState {
    if (!this.isCategoryVisible(category)) {
      return ConsentState.UNSPECIFIED;
    }
    const prefValues = this.getDestinationsForCategory(category).map(
      (d) => this.state.prefs[d.id] || ConsentState.UNSPECIFIED
    );
    assert(
      new Set(prefValues).size === 1,
      `Expected exactly one unique preference for ${category}, got ${JSON.stringify(prefValues)}`
    );
    return prefValues[0];
  }

  private isCategoryVisible(category: DestinationCategory): boolean {
    return this.getDestinationsForCategory(category).length > 0;
  }

  private isInputValid(): boolean {
    const essentialDestinationIds = this.getDestinationsForCategory(DestinationCategory.ESSENTIAL).map(
      (d) => d.id
    );
    return Object.entries(this.state.prefs).every(
      ([destinationId, consentState]) =>
        consentState === ConsentState.EXPLICIT_AGREE ||
        consentState === ConsentState.EXPLICIT_DISAGREE ||
        essentialDestinationIds.includes(destinationId as DestinationId)
    );
  }

  private renderPrefPicker(category: DestinationCategory): JSX.Element {
    return (
      <RadioButtonSelector
        selectedValue={this.getCategoryPreference(category)}
        options={[
          {label: 'Yes', value: ConsentState.EXPLICIT_AGREE},
          {label: 'No', value: ConsentState.EXPLICIT_DISAGREE},
        ]}
        onChange={(value: ConsentState) => {
          this.handleSetPreference(category, value);
        }}
      />
    );
  }

  private handleCancel = async (): Promise<void> => {
    const continuedWithCancel = await Promise.resolve(
      modals.confirm(
        'Are you sure you want to cancel?',
        <span>
          Your preferences have not been saved. By continuing to use our website, you're agreeing to our{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          .
        </span>,
        ButtonStyle.PRIMARY,
        {
          acceptText: 'Yes, Cancel',
          rejectText: 'Go Back',
          enableRedesign: true,
          promiseCompatible: true,
          medium: true,
        }
      )
    );
    if (continuedWithCancel) {
      const implicitAgreePrefs = {...this.props.initialPreferences};
      for (const destinationId of this.props.destinationIds) {
        assert(
          implicitAgreePrefs[destinationId] !== ConsentState.UNSPECIFIED,
          'Initial prefs passed into CookieConsentModal should have have value UNSPECIFIED.'
        );
        if (!implicitAgreePrefs[destinationId]) {
          implicitAgreePrefs[destinationId] = ConsentState.IMPLICIT_AGREE;
        }
      }
      this.props.onRequestSubmit(implicitAgreePrefs);
    }
  };

  override render(): JSX.Element {
    return (
      <Modal
        buttons={[
          {text: 'Save', submit: true, disabled: !this.isInputValid()},
          {text: 'Cancel', cancel: true},
        ]}
        closeable={false}
        large={true}
        onRequestClose={this.handleCancel}
        onRequestSubmit={() => {
          this.props.onRequestSubmit(this.state.prefs);
        }}
        title="Cookie Preferences"
      >
        <p className={css(styles.explanationText)}>
          Benchling uses data collected by cookies to improve your browsing experience, analyze site traffic,{' '}
          {this.isCategoryVisible(DestinationCategory.ADVERTISING) && 'deliver personalized advertisements, '}
          and increase the overall performance of our site. By using our website, you agree to our{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          . The table below outlines how we use this data by category. To opt out of a category of data
          collection, select “No” and save your preferences.
        </p>

        <table className={css(styles.table)}>
          <thead>
            <tr>
              <Heading>Allow</Heading>
              <Heading>Category</Heading>
              <Heading>Purpose</Heading>
            </tr>
          </thead>
          <tbody>
            {this.isCategoryVisible(DestinationCategory.FUNCTIONAL) && (
              <tr {...getTestProps({component: 'CookieConsentModal', element: 'functionalCategory'})}>
                <Cell dataTestAttrs={{component: 'CookieConsentModal', element: 'functionalPreference'}}>
                  {this.renderPrefPicker(DestinationCategory.FUNCTIONAL)}
                </Cell>
                <Cell>Functional</Cell>
                <Cell>
                  To monitor the performance of our site and to enable features like chat-based support.
                </Cell>
              </tr>
            )}
            {this.isCategoryVisible(DestinationCategory.MARKETING_AND_ANALYTICS) && (
              <tr
                {...getTestProps({component: 'CookieConsentModal', element: 'marketingAndAnalyticsCategory'})}
              >
                <Cell
                  dataTestAttrs={{
                    component: 'CookieConsentModal',
                    element: 'marketingAndAnalyticsPreference',
                  }}
                >
                  {this.renderPrefPicker(DestinationCategory.MARKETING_AND_ANALYTICS)}
                </Cell>
                <Cell>Marketing and Analytics</Cell>
                <Cell>
                  To understand user behavior in order to provide you with a more relevant browsing experience
                  or personalize the content on our site.
                </Cell>
              </tr>
            )}
            {this.isCategoryVisible(DestinationCategory.ADVERTISING) && (
              <tr {...getTestProps({component: 'CookieConsentModal', element: 'advertisingCategory'})}>
                <Cell dataTestAttrs={{component: 'CookieConsentModal', element: 'advertisingPreference'}}>
                  {this.renderPrefPicker(DestinationCategory.ADVERTISING)}
                </Cell>
                <Cell>Advertising</Cell>
                <Cell>
                  To personalize and measure the effectiveness of advertising on our site and other websites.
                  Some services are also used for marketing and analytics.
                </Cell>
              </tr>
            )}
            <tr {...getTestProps({component: 'CookieConsentModal', element: 'essentialCategory'})}>
              <Cell>N/A</Cell>
              <Cell>Essential</Cell>
              <Cell>
                We use browser cookies and other services that are necessary for the site to work as intended.
              </Cell>
            </tr>
          </tbody>
        </table>
      </Modal>
    );
  }
}

const CELL_STYLES: CSSProperties = {
  borderStyle: 'solid',
  borderWidth: 1,
  borderColor: GRAY,
  padding: 8,
};

const styles = StyleSheet.create({
  table: {
    width: '100%',
  },
  explanationText: LEGACY_FONT_PROXIMA_13,
  tableHeading: {
    ...LEGACY_FONT_PROXIMA_TITLE,
    ...CELL_STYLES,
  },
  tableCell: {
    ...LEGACY_FONT_PROXIMA_13,
    ...CELL_STYLES,
  },
});
